// CSV bulk import dialog for products.
//
// Flow:
//   1. User downloads template (optional) or picks a CSV file.
//   2. We parse it, validate each row, and show a preview table
//      with valid (green) and invalid (red) rows + the error reason.
//   3. On confirm, we commit only the valid rows via the batched
//      bulkCreateProducts() call. Invalid rows are reported back.
//
// Validation:
//   - Required: sku (non-empty, unique), name (non-empty), price (> 0)
//   - Optional: costPrice (>= 0), categoryId (string), description (string),
//     imageUrl (URL), isActive (default true)
//   - Uniqueness: SKU must not collide with any existing product in Firestore
//     OR with another row in the same CSV (first wins; later duplicates flagged)
//
// Owner-only — gated by the calling route.

import { useMemo, useRef, useState } from 'react';
import {
  Upload,
  Download,
  FileText,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { useAuth } from '@/hooks/useAuth';
import { useProducts, useUpsertProducts } from '@/hooks/useProducts';
import { useCategories } from '@/hooks/useCategories';
import type { Category } from '@/types/domain';
import { parseCsv, type CsvParseResult } from '@/lib/csv';
import { PRODUCT_TEMPLATE, downloadTemplate } from '@/lib/importTemplates';
import { PRODUCT_COLUMNS } from '@/lib/api/productsExport';
import { formatIDR } from '@/lib/format';
import type { BulkCreateProductItem } from '@/lib/api/products';
import { IMPORT_ACCEPT, spreadsheetFileToText } from '@/lib/spreadsheet';

export interface CsvImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Optional callback after a successful import (e.g. for toast). */
  onImported?: (count: number) => void;
}

type RowStatus = 'valid' | 'update' | 'invalid' | 'duplicate-in-file';

// The owner's catalogue sheet heads its columns "Product Name", "Sub Kategori"
// and so on, where the template says name and category. Both are read -- the
// first non-empty one wins -- so neither sheet has to be retyped to import.
// Lowercase, because csv.ts lowercases every header.
const NAME_KEYS = ['name', 'product name', 'nama produk'];
const CATEGORY_KEYS = ['category', 'sub kategori', 'kategori', 'categoryid'];
const BRAND_KEYS = ['brand', 'merek', 'merk'];
const UNIT_KEYS = ['unit', 'satuan'];
const PRICE_KEYS = ['price', 'harga'];

const firstOf = (raw: Record<string, string>, keys: readonly string[]): string =>
  keys.map((k) => raw[k] ?? '').find(Boolean) ?? '';

/**
 * The category as the file wrote it. A name that matches no category is not an
 * error -- the row imports without one -- but it is worth seeing before
 * confirming, so it is marked instead of shown as though it had matched.
 */
function CategoryCell({ text, matched }: { text: string; matched: boolean }) {
  if (!text) return <span className="text-ink-subtle">—</span>;
  if (matched) return <span className="text-ink">{text}</span>;
  return (
    <span
      className="text-warning"
      title="Belum ada kategori dengan nama ini — produk diimpor tanpa kategori"
    >
      {text} (belum ada)
    </span>
  );
}

interface ValidatedRow {
  rowNumber: number;
  raw: Record<string, string>;
  parsed: BulkCreateProductItem | null;
  error?: string;
  status: RowStatus;
}


const ACCEPT = IMPORT_ACCEPT;

export function CsvImportDialog({ open, onClose, onImported }: CsvImportDialogProps) {
  const { user } = useAuth();
  const { data: products } = useProducts();
  const upsert = useUpsertProducts();
  const { data: categories } = useCategories({ isActive: true });

  /**
   * Category name → id, matching the DB's own `categories_name_key on
   * (lower(name))`. Returns undefined for a name we do not know, which imports
   * the product with no category rather than inventing one.
   */
  const resolveCategory = useMemo(() => {
    const byName = new Map<string, string>((categories ?? []).map((c: Category) => [c.name.trim().toLowerCase(), c.id]));
    return (raw: string): string | undefined => {
      const t = raw.trim();
      if (!t) return undefined;
      return byName.get(t.toLowerCase());
    };
  }, [categories]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parseResult, setParseResult] = useState<CsvParseResult | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Reset on close so re-opening starts fresh.
  const reset = () => {
    setParseResult(null);
    setFileName(null);
    setImportError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (upsert.isPending) return; // block close mid-import
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    setImportError(null);
    setFileName(file.name);
    try {
      const text = await spreadsheetFileToText(file);
      const result = parseCsv(text);
      setParseResult(result);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Gagal membaca file');
      setParseResult(null);
    }
  };

  const handlePickFile = () => fileInputRef.current?.click();

  // From the shared source, so the template cannot drift from the parser or
  // from the export's column order.
  const handleDownloadTemplate = async () => {
    setImportError(null);
    try {
      await downloadTemplate(PRODUCT_TEMPLATE);
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Gagal membuat template');
    }
  };

  // Validate parsed rows against schema + DB SKUs.
  const validated = useMemo<ValidatedRow[]>(() => {
    if (!parseResult) return [];
    const dbSkus = new Set((products ?? []).map((p) => p.sku.toLowerCase()));
    const seenInFile = new Set<string>();
    const rows: ValidatedRow[] = [];

    for (const raw of parseResult.rows) {
      const rowNum = Number(raw.__row ?? '0');
      const get = (k: string): string => raw[k.toLowerCase()] ?? '';

      const sku = get('sku');
      const name = firstOf(raw, NAME_KEYS);
      const priceStr = firstOf(raw, PRICE_KEYS);

      const errors: string[] = [];
      if (!sku) errors.push('SKU kosong');
      if (!name) errors.push('Nama kosong');
      if (!priceStr) errors.push('Price kosong');

      const price = Number(priceStr);
      if (priceStr && (Number.isNaN(price) || !Number.isFinite(price))) {
        errors.push('Price bukan angka');
      } else if (price <= 0) {
        errors.push('Price harus > 0');
      }

      const costPriceStr = get('costprice');
      let costPrice: number | undefined;
      if (costPriceStr) {
        const n = Number(costPriceStr);
        if (Number.isNaN(n) || n < 0) {
          errors.push('CostPrice harus angka ≥ 0');
        } else {
          costPrice = n;
        }
      }

      const isActiveRaw = get('isactive').toLowerCase();
      let isActive = true;
      if (isActiveRaw && !['true', 'false', '1', '0', 'yes', 'no'].includes(isActiveRaw)) {
        errors.push('isActive harus true/false');
      } else if (isActiveRaw === 'false' || isActiveRaw === '0' || isActiveRaw === 'no') {
        isActive = false;
      }

      const imageUrl = get('imageurl');
      // An exported product carries its stored image path ("products/…"),
      // which must import back unchanged; anything else must be a URL.
      if (imageUrl && !/^https?:\/\//i.test(imageUrl) && !/^products\//.test(imageUrl)) {
        errors.push('imageUrl harus URL (http/https) atau path gambar dari export');
      }

      // An existing SKU is no longer a rejection: it is the update half of
      // export → edit → re-import, which is the whole reason this dialog exists.
      // A SKU repeated inside ONE file is still an error — the two rows would
      // fight over the same product and the last one would silently win.
      let status: RowStatus = 'valid';
      if (errors.length > 0) {
        status = 'invalid';
      } else {
        const skuLc = sku.toLowerCase();
        if (seenInFile.has(skuLc)) {
          errors.push('SKU muncul dua kali di file ini');
          status = 'duplicate-in-file';
        } else {
          seenInFile.add(skuLc);
          if (dbSkus.has(skuLc)) status = 'update';
        }
      }

      const item: BulkCreateProductItem | null = errors.length === 0
        ? {
            sku: sku.trim(),
            name: name.trim(),
            price,
            costPrice,
            // Resolved from a NAME. Passing a name straight through would hit
            // rowFrom's UUID test, fall through to `legacy_category`, and create
            // an orphan category that only shows up in CategoryManager. An
            // unknown name imports with no category, by the owner's choice.
            categoryId: resolveCategory(firstOf(raw, CATEGORY_KEYS)),
            unit: firstOf(raw, UNIT_KEYS) || undefined,
            brand: firstOf(raw, BRAND_KEYS) || undefined,
            imageUrl: imageUrl || undefined,
            description: get('description') || undefined,
            isActive,
          }
        : null;

      rows.push({
        rowNumber: rowNum,
        raw,
        parsed: item,
        error: errors[0],
        status,
      });
    }

    return rows;
  }, [parseResult, products, resolveCategory]);

  const validRows = validated.filter((r) => r.status === 'valid' || r.status === 'update');
  const invalidRows = validated.filter((r) => r.status !== 'valid' && r.status !== 'update');
  const updateRows = validated.filter((r) => r.status === 'update');
  const createRows = validated.filter((r) => r.status === 'valid');

  const handleImport = async () => {
    if (!user || validRows.length === 0) return;
    setImportError(null);
    try {
      const result = await upsert.mutateAsync(
        validRows.map((r) => r.parsed!).filter(Boolean),
      );
      if (result.failed.length > 0) {
        setImportError(
          `${result.failed.length} baris gagal: ` +
            result.failed.slice(0, 3).map((f) => `${f.sku} (${f.reason})`).join('; '),
        );
        return;
      }
      onImported?.(result.created + result.updated);
      handleClose();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import gagal');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Import produk"
      description="Pilih file Excel (.xlsx) atau CSV. SKU yang sudah ada akan diperbarui, yang baru ditambahkan. Baris tidak valid dilewati dan dilaporkan di preview."
      size="xl"
      closeOnBackdrop={!upsert.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={upsert.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon={
              upsert.isPending ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />
            }
            onClick={handleImport}
            disabled={!parseResult || validRows.length === 0 || upsert.isPending}
          >
            {upsert.isPending
              ? 'Importing…'
              : validRows.length > 0
                ? `Import ${validRows.length} produk (${createRows.length} baru, ${updateRows.length} update)`
                : 'Import'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Actions row */}
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
          />
          <Button variant="secondary" leftIcon={<Upload size={16} />} onClick={handlePickFile}>
            Pilih file Excel / CSV
          </Button>
          <Button variant="ghost" leftIcon={<Download size={16} />} onClick={() => void handleDownloadTemplate()}>
            Download template Excel
          </Button>
          {fileName && (
            <span className="inline-flex items-center gap-1.5 text-caption text-ink-subtle">
              <FileText size={13} />
              {fileName}
              <button
                type="button"
                onClick={reset}
                className="ml-1 text-neutral hover:text-ink transition-colors"
                aria-label="Hapus file"
              >
                <XCircle size={14} />
              </button>
            </span>
          )}
        </div>

        {/* Format hint */}
        <div className="p-3 rounded bg-canvas border border-line text-caption text-ink-subtle">
          <p className="font-medium text-ink mb-1">Format kolom CSV (header baris pertama):</p>
          <code className="font-mono text-[11px] block break-all">
            {PRODUCT_COLUMNS.join(', ')}
          </code>
          <p className="mt-1.5">
            Wajib: <strong>sku</strong>, <strong>name</strong>, <strong>price</strong> ({'>'} 0).
            Opsional: costPrice (≥ 0), unit (pcs/box), brand, description, imageUrl
            (http/https), isActive (true/false, default true).
          </p>
          <p className="mt-1.5">
            Judul kolom dari sheet katalog juga dikenali: <strong>Product Name</strong> atau
            Nama Produk untuk name, <strong>Sub Kategori</strong> atau Kategori untuk category,
            Merek untuk brand, Satuan untuk unit, Harga untuk price.
          </p>
          <p className="mt-1.5">
            <strong>category</strong> diisi <em>nama</em> kategori. Nama yang belum ada
            di sistem akan diimpor tanpa kategori, bukan ditolak.
          </p>
          <p className="mt-1.5">
            SKU yang sudah ada akan <strong>diperbarui</strong>, bukan dilewati — jadi
            hasil Export bisa diedit lalu di-import kembali. SKU sendiri tidak pernah
            diubah oleh import.
          </p>
        </div>

        {/* Error from import or parser */}
        {importError && (
          <div className="flex items-start gap-2 p-3 rounded bg-danger/10 text-danger text-small">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{importError}</span>
          </div>
        )}
        {parseResult && parseResult.parseErrors.length > 0 && (
          <div className="flex flex-col gap-1 p-3 rounded bg-warning/10 text-warning text-small">
            <p className="font-medium flex items-center gap-1.5">
              <AlertTriangle size={14} /> Parse warning:
            </p>
            <ul className="list-disc pl-5">
              {parseResult.parseErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview */}
        {parseResult && (
          <>
            <div className="flex items-center gap-3">
              <Chip variant="success">
                <CheckCircle2 size={12} />
                {createRows.length} baru · {updateRows.length} update
              </Chip>
              {invalidRows.length > 0 && (
                <Chip variant="danger">
                  <XCircle size={12} />
                  {invalidRows.length} invalid
                </Chip>
              )}
              <span className="text-caption text-ink-subtle">
                Total {validated.length} baris data
              </span>
            </div>

            {validated.length === 0 ? (
              <div className="text-center py-8 text-ink-subtle text-small">
                File CSV tidak berisi baris data (hanya header?).
              </div>
            ) : (
              <div className="border border-line rounded overflow-x-auto max-h-72">
                <table className="w-full text-small">
                  <thead className="bg-canvas/70 sticky top-0 text-overline uppercase text-ink-subtle font-medium tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">SKU</th>
                      <th className="px-3 py-2 text-left">Nama</th>
                      <th className="px-3 py-2 text-left">Kategori</th>
                      <th className="px-3 py-2 text-left">Brand</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validated.map((r) => (
                      <tr
                        key={r.rowNumber}
                        className={
                          r.status === 'valid'
                            ? 'border-t border-line bg-success/5'
                            : r.status === 'update'
                              ? 'border-t border-line bg-info/5'
                              : 'border-t border-line bg-danger/5'
                        }
                      >
                        <td className="px-3 py-2 text-caption text-ink-subtle">{r.rowNumber}</td>
                        <td className="px-3 py-2 font-mono text-caption">{r.raw['sku'] || '—'}</td>
                        <td className="px-3 py-2 text-ink truncate max-w-xs">
                          {firstOf(r.raw, NAME_KEYS) || '—'}
                        </td>
                        <td className="px-3 py-2 text-caption whitespace-nowrap">
                          <CategoryCell
                            text={firstOf(r.raw, CATEGORY_KEYS)}
                            matched={!!resolveCategory(firstOf(r.raw, CATEGORY_KEYS))}
                          />
                        </td>
                        <td className="px-3 py-2 text-caption text-ink truncate max-w-[10rem]">
                          {firstOf(r.raw, BRAND_KEYS) || '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-medium whitespace-nowrap">
                          {r.parsed ? formatIDR(r.parsed.price) : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {r.status === 'valid' ? (
                            <span className="inline-flex items-center gap-1 text-success text-caption font-medium">
                              <CheckCircle2 size={12} /> Baru
                            </span>
                          ) : r.status === 'update' ? (
                            <span className="inline-flex items-center gap-1 text-info text-caption font-medium">
                              <CheckCircle2 size={12} /> Update
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-danger text-caption font-medium">
                              <XCircle size={12} /> {r.error}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {!parseResult && !fileName && (
          <div className="border-2 border-dashed border-line rounded-lg p-8 text-center text-ink-subtle">
            <Upload size={28} className="mx-auto mb-2 text-neutral" strokeWidth={1.5} />
            <p className="text-small">Belum ada file dipilih.</p>
            <p className="text-caption mt-1">
              Klik <strong>Pilih file Excel / CSV</strong> di atas, atau download template untuk mulai.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
